import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { SOCKET_URL, API_BASE_URL } from '../config';
import Navigation from '../components/Navigation';
import LiveGamesSidebar from '../components/LiveGamesSidebar';
import { DirectDOMIconManager } from '../components/game/PossessionIcon';
import BettingPanel from '../components/game/BettingPanel';
import Toast from '../components/Toast';
import { Wallet } from '../utils/wallet';
import { getTeamAveragePoints, selectPlayers } from '../utils/bettingUtils';
import { useStandings } from '../hooks/useStandings';

interface Team {
  id: string;
  name: string;
  color: string;
}

interface GameTeams {
  team1: Team;
  team2: Team;
}

interface PlayerStats {
  id?: string;
  name: string;
  mechClass: string;
  points: number;
  rebounds: number;
  assists: number;
}

interface Score {
  team1: number;
  team2: number;
}

interface Possession {
  possessionNumber: number;
  team: string;
  type: 'score' | 'miss' | 'turnover' | 'offensive_rebound';
  description: string;
  shotType?: number;
  points?: number;
}

interface GameData {
  id: string;
  status: 'live' | 'completed';
  teams: GameTeams;
  score: Score;
  currentPossession: number;
  totalPossessions: number;
  possessionLog: Possession[];
  playerStats: {
    team1: PlayerStats[];
    team2: PlayerStats[];
  };
  startTime?: string;
  endTime?: string;
  currentPossessionTeam?: 'team1' | 'team2';
  slot?: number;
}

interface NextGame {
  team1: Team;
  team2: Team;
}

interface ToastData {
  id: string;
  message: string;
  type: 'success' | 'info';
}

interface RostersPlayerStats {
  id: string;
  name: string;
  teamId: string;
  avgPoints: number;
  avgAssists: number;
  gamesPlayed: number;
}

interface RostersData {
  [teamId: string]: RostersPlayerStats[];
}

const GamesPage: React.FC = () => {
  const location = useLocation();
  const [activeGames, setActiveGames] = useState<(GameData | null)[]>([null, null]);
  const [completedGames, setCompletedGames] = useState<(GameData | null)[]>([null, null]);
  const [nextGames, setNextGames] = useState<(NextGame | null)[]>([null, null]);
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('Connecting...');
  const [credits, setCredits] = useState<number>(Wallet.getCredits());
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [rosters, setRosters] = useState<RostersData>({});
  const { getTeamStanding } = useStandings();

  // Fetch rosters data
  useEffect(() => {
    const fetchRosters = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/rosters`);
        const data = await response.json();
        if (data.success && data.rosters) {
          setRosters(data.rosters);
        }
      } catch (error) {
        console.error('Error fetching rosters:', error);
      }
    };
    fetchRosters();
  }, []);

  // Helper function to get winning team ID
  const getWinningTeamId = useCallback((game: GameData) => {
    if (game.score.team1 > game.score.team2) {
      return game.teams.team1.id;
    } else if (game.score.team2 > game.score.team1) {
      return game.teams.team2.id;
    }
    return null;
  }, []);

  // Handle credits update callback
  const handleCreditsUpdate = useCallback((newCredits: number) => {
    setCredits(newCredits);
  }, []);

  // Toast management
  const showToast = useCallback((message: string, type: 'success' | 'info') => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    setToasts(prev => [...prev, { id, message, type }]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Check if a bet matches a completed game
  const findMatchingBet = useCallback((completedGame: GameData, slot: number, allBets: any) => {
    let betGameId: string | null = null;
    let betsFound: any = null;
    
    console.log(`🔍 [FIND MATCH] Looking for bets matching game: ${completedGame.id}, slot: ${slot}`);
    console.log(`🔍 [FIND MATCH] Teams: ${completedGame.teams.team1.id} vs ${completedGame.teams.team2.id}`);
    
    // Try direct game ID lookup (migrated bets or bets placed on active game)
    const directBets = Wallet.getBetsForGame(completedGame.id);
    console.log(`🔍 [FIND MATCH] Direct lookup for ${completedGame.id}:`, directBets);
    if (Object.keys(directBets).length > 0) {
      betGameId = completedGame.id;
      betsFound = directBets;
      console.log(`✅ [FIND MATCH] Found direct match: ${betGameId}`);
      return { betGameId, betsFound };
    }
    
    // Try next game ID pattern (if migration didn't happen)
    const nextGameId = `next-${slot}-${completedGame.teams.team1.id}-${completedGame.teams.team2.id}`;
    const nextBets = Wallet.getBetsForGame(nextGameId);
    console.log(`🔍 [FIND MATCH] Next game ID lookup for ${nextGameId}:`, nextBets);
    if (Object.keys(nextBets).length > 0) {
      betGameId = nextGameId;
      betsFound = nextBets;
      console.log(`✅ [FIND MATCH] Found next game match: ${betGameId}`);
      return { betGameId, betsFound };
    }
    
    // Try reversed team order (in case teams were swapped)
    const reversedGameId = `next-${slot}-${completedGame.teams.team2.id}-${completedGame.teams.team1.id}`;
    const reversedBets = Wallet.getBetsForGame(reversedGameId);
    console.log(`🔍 [FIND MATCH] Reversed game ID lookup for ${reversedGameId}:`, reversedBets);
    if (Object.keys(reversedBets).length > 0) {
      betGameId = reversedGameId;
      betsFound = reversedBets;
      console.log(`✅ [FIND MATCH] Found reversed match: ${betGameId}`);
      return { betGameId, betsFound };
    }
    
    // Search through all bets to find a match by team IDs
    // BUT: Only for next-* patterns, NOT for continuous-* games (they must match by exact ID)
    // This handles cases where bets were placed on next-* games that haven't been migrated
    console.log(`🔍 [FIND MATCH] Searching all ${Object.keys(allBets).length} bet game IDs for team match (next-* only)`);
    for (const [gameId, bets] of Object.entries(allBets)) {
      // Skip continuous-* games - they must match by exact ID only
      if (gameId.startsWith('continuous-')) {
        continue;
      }
      
      const gameBets = bets as { [teamId: string]: number };
      const teamIds = Object.keys(gameBets);
      const hasTeam1 = teamIds.includes(completedGame.teams.team1.id);
      const hasTeam2 = teamIds.includes(completedGame.teams.team2.id);
      
      // Also check for over/under bets that might have team IDs in the bet keys
      const hasTeam1PtsBet = gameBets['team1-pts-over'] || gameBets['team1-pts-under'];
      const hasTeam2PtsBet = gameBets['team2-pts-over'] || gameBets['team2-pts-under'];
      
      // Only match if it's a next-* pattern and teams match
      if (gameId.startsWith('next-') && (hasTeam1 && hasTeam2)) {
        betGameId = gameId;
        betsFound = gameBets;
        console.log(`✅ [FIND MATCH] Found next-* team-based match: ${betGameId}`, gameBets);
        return { betGameId, betsFound };
      }
    }
    
    console.log(`❌ [FIND MATCH] No match found for game ${completedGame.id}`);
    return { betGameId: null, betsFound: null };
  }, []);

  // Check all bets against completed games
  const checkAllBetsAgainstCompletedGames = useCallback(async (completedGamesList: (GameData | null)[], showToastCallback: (message: string, type: 'success' | 'info') => void) => {
    console.log('🔍 [BET CHECK] Starting bet check against completed games');
    console.log('🔍 [BET CHECK] Toast callback provided:', typeof showToastCallback === 'function');
    
    // Validate callback
    if (typeof showToastCallback !== 'function') {
      console.error('❌ [BET CHECK] Invalid toast callback provided!');
      return;
    }
    
    // Get all bets first
    const allBets = Wallet.getAllBets();
    console.log('🔍 [BET CHECK] All bets in storage:', Object.keys(allBets));
    
    if (Object.keys(allBets).length === 0) {
      console.log('🔍 [BET CHECK] No bets to check');
      return; // No bets to check
    }

    // Filter out null games
    let validCompletedGames = completedGamesList.filter((game): game is GameData => {
      return game !== null && game !== undefined;
    });

    // If no completed games in socket state, try to fetch recent ones from API
    if (validCompletedGames.length === 0) {
      console.log('🔍 [BET CHECK] No completed games in socket state, fetching from API...');
      try {
        const response = await fetch(`${API_BASE_URL}/games/recent-completed?hours=168`); // Check last 7 days
        const data = await response.json();
        if (data.success && data.games) {
          // Convert database games to GameData format
          validCompletedGames = data.games.map((dbGame: any) => {
            try {
              const simDetails = typeof dbGame.simulation_details === 'string' 
                ? JSON.parse(dbGame.simulation_details) 
                : dbGame.simulation_details;
              
              // Extract game ID from simulation details if available
              // The gameId is stored in fullResult.gameId when saved from continuous simulator
              // Note: simulation_details IS the fullResult object, so we access gameId directly
              let gameId = simDetails?.gameId || 
                          simDetails?.id || 
                          `db-${dbGame.id}`;
              
              // Extract slot from continuous game ID if present
              let slot = 0;
              if (gameId.startsWith('continuous-')) {
                const parts = gameId.split('-');
                if (parts.length >= 2) {
                  slot = parseInt(parts[1]) || 0;
                }
              }
              
              console.log(`🔍 [BET CHECK] Converted DB game ${dbGame.id} to gameId: ${gameId}, slot: ${slot}`);
              console.log(`🔍 [BET CHECK] Teams: ${dbGame.home_team_id} vs ${dbGame.away_team_id}, Score: ${dbGame.home_score}-${dbGame.away_score}`);
              
              return {
                id: gameId,
                status: 'completed' as const,
                teams: {
                  team1: { 
                    id: dbGame.home_team_id, 
                    name: simDetails?.teams?.team1?.name || dbGame.home_team_id 
                  },
                  team2: { 
                    id: dbGame.away_team_id, 
                    name: simDetails?.teams?.team2?.name || dbGame.away_team_id 
                  }
                },
                score: {
                  team1: dbGame.home_score,
                  team2: dbGame.away_score
                },
                playerStats: {
                  team1: simDetails?.outcome?.team1?.playerStats || [],
                  team2: simDetails?.outcome?.team2?.playerStats || []
                },
                endTime: dbGame.game_date,
                slot: slot
              };
            } catch (e) {
              console.error('Error parsing game data:', e);
              return null;
            }
          }).filter((game: GameData | null): game is GameData => game !== null);
          
          console.log(`🔍 [BET CHECK] Fetched ${validCompletedGames.length} completed games from API`);
        }
      } catch (error) {
        console.error('Error fetching recent completed games:', error);
      }
    }

    if (validCompletedGames.length === 0) {
      console.log('🔍 [BET CHECK] No completed games found to check against');
      // Still check if any bets match game IDs directly (in case games completed but aren't in the list)
      for (const [betGameId, bets] of Object.entries(allBets)) {
        // Check if this looks like a continuous game ID
        if (betGameId.startsWith('continuous-')) {
          console.log(`🔍 [BET CHECK] Found bet on game ID: ${betGameId}, but no matching completed game found`);
        }
      }
      return;
    }

    console.log(`🔍 [BET CHECK] Checking ${validCompletedGames.length} completed games`);

    // Check each completed game against bets
    for (const completedGame of validCompletedGames) {
      console.log(`🔍 [BET CHECK] Checking game: ${completedGame.id}, teams: ${completedGame.teams.team1.name} vs ${completedGame.teams.team2.name}`);
      
      // Refresh bets in case previous iteration cleared some
      const currentBets = Wallet.getAllBets();
      if (Object.keys(currentBets).length === 0) {
        console.log('🔍 [BET CHECK] No more bets remaining');
        break; // No more bets to check
      }

      const { betGameId, betsFound } = findMatchingBet(completedGame, completedGame.slot || 0, currentBets);
      
      console.log(`🔍 [BET CHECK] Match result for game ${completedGame.id}:`, { betGameId, hasBets: !!betsFound });
      
      if (betGameId && betsFound) {
        // CRITICAL: Only process if game IDs match OR if it's a next-* pattern that matches
        // For continuous-* games, the betGameId MUST match completedGame.id exactly
        if (betGameId.startsWith('continuous-') && betGameId !== completedGame.id) {
          console.log(`⚠️ [BET CHECK] Skipping - bet game ID ${betGameId} doesn't match completed game ID ${completedGame.id}`);
          continue; // Skip this match - it's a different game
        }
        
        console.log(`💰 [BET CHECK] Processing bets for game ${betGameId} against completed game ${completedGame.id}`);
        console.log(`💰 [BET CHECK] Bets found:`, betsFound);
        
        // Calculate team averages and get selected players for over/under bets
        const team1Standing = getTeamStanding(completedGame.teams.team1.id);
        const team2Standing = getTeamStanding(completedGame.teams.team2.id);
        const team1AvgPoints = getTeamAveragePoints(team1Standing);
        const team2AvgPoints = getTeamAveragePoints(team2Standing);
        
        // Get selected players for this game
        const team1Players = rosters[completedGame.teams.team1.id] || [];
        const team2Players = rosters[completedGame.teams.team2.id] || [];
        const selectedPlayers = selectPlayers(betGameId, team1Players, team2Players);
        
        // Process all bets (outcome and over/under)
        const payoutResults = Wallet.processAllBetsForGame(
          betGameId,
          completedGame,
          team1AvgPoints,
          team2AvgPoints,
          {
            team1Points: selectedPlayers.team1Points ? {
              id: selectedPlayers.team1Points.id,
              avgPoints: selectedPlayers.team1Points.avgPoints
            } : null,
            team1Assists: selectedPlayers.team1Assists ? {
              id: selectedPlayers.team1Assists.id,
              avgAssists: selectedPlayers.team1Assists.avgAssists
            } : null,
            team2Points: selectedPlayers.team2Points ? {
              id: selectedPlayers.team2Points.id,
              avgPoints: selectedPlayers.team2Points.avgPoints
            } : null,
            team2Assists: selectedPlayers.team2Assists ? {
              id: selectedPlayers.team2Assists.id,
              avgAssists: selectedPlayers.team2Assists.avgAssists
            } : null
          }
        );
        
        console.log(`💰 [BET CHECK] Payout results:`, payoutResults);
        console.log(`💰 [BET CHECK] Payout results count:`, payoutResults.length);
        
        // Update credits
        const finalCredits = Wallet.getCredits();
        handleCreditsUpdate(finalCredits);
        
        // Show toasts for each payout result
        if (payoutResults.length > 0) {
          console.log(`💰 [BET CHECK] Showing ${payoutResults.length} toast notifications`);
          payoutResults.forEach((result, index) => {
            console.log(`💰 [BET CHECK] Processing payout result ${index + 1}/${payoutResults.length}:`, result);
            if (result.betType === 'outcome') {
          if (result.won) {
                const winningTeamName = completedGame.teams.team1.id === result.betKey
              ? completedGame.teams.team1.name 
              : completedGame.teams.team2.name;
                console.log(`💰 [BET CHECK] Showing win toast for ${winningTeamName}`);
            showToastCallback(
              `Won ${result.payout} credits on ${winningTeamName}! New total: ${result.newTotal}`,
              'success'
            );
          } else {
                console.log(`💰 [BET CHECK] Showing loss toast`);
            showToastCallback(
              `Lost bet. Credits: ${result.newTotal}`,
              'info'
            );
          }
            } else if (result.betType === 'over-under') {
              if (result.won) {
                console.log(`💰 [BET CHECK] Showing over/under win toast`);
                showToastCallback(
                  `Won ${result.payout} credits on over/under bet! New total: ${result.newTotal}`,
                  'success'
                );
              } else if (result.payout > 0) {
                // Push (refund)
                console.log(`💰 [BET CHECK] Showing push/refund toast`);
                showToastCallback(
                  `Push on over/under bet. Refunded ${result.payout} credits. New total: ${result.newTotal}`,
                  'info'
                );
        } else {
                console.log(`💰 [BET CHECK] Showing over/under loss toast`);
                showToastCallback(
                  `Lost over/under bet. Credits: ${result.newTotal}`,
                  'info'
                );
              }
            } else {
              console.warn(`💰 [BET CHECK] Unknown bet type: ${result.betType}`);
            }
          });
        } else {
          console.log(`💰 [BET CHECK] No payout results for game ${betGameId}`);
        }
      }
    }
  }, [findMatchingBet, getWinningTeamId, handleCreditsUpdate, getTeamStanding, rosters]);

  // Handle payout when game completes
  const handleGamePayout = useCallback((completedGame: GameData, slot: number, showToastCallback?: (message: string, type: 'success' | 'info') => void) => {
    console.log('💰 [PAYOUT] Processing payout for game:', {
      gameId: completedGame.id,
      slot,
      team1: completedGame.teams.team1.name,
      team2: completedGame.teams.team2.name,
      score: `${completedGame.score.team1}-${completedGame.score.team2}`
    });
    
    let betGameId: string | null = null;
    let betsFound: any = null;
    
    // Get all bets to help debug
    const allBets = Wallet.getAllBets();
    console.log('💰 [PAYOUT] All current bets in wallet:', allBets);
    
    // Try direct game ID lookup (migrated bets)
    const directBets = Wallet.getBetsForGame(completedGame.id);
    console.log('💰 [PAYOUT] Direct bets lookup:', {
      gameId: completedGame.id,
      bets: directBets,
      hasBets: Object.keys(directBets).length > 0
    });
    
    if (Object.keys(directBets).length > 0) {
      betGameId = completedGame.id;
      betsFound = directBets;
    } else {
      // Try next game ID pattern (if migration didn't happen)
      const nextGameId = `next-${slot}-${completedGame.teams.team1.id}-${completedGame.teams.team2.id}`;
      const nextBets = Wallet.getBetsForGame(nextGameId);
      console.log('💰 [PAYOUT] Next game ID lookup:', {
        nextGameId,
        bets: nextBets,
        hasBets: Object.keys(nextBets).length > 0
      });
      
      if (Object.keys(nextBets).length > 0) {
        betGameId = nextGameId;
        betsFound = nextBets;
      } else {
        // Try reversed team order (in case teams were swapped)
        const reversedGameId = `next-${slot}-${completedGame.teams.team2.id}-${completedGame.teams.team1.id}`;
        const reversedBets = Wallet.getBetsForGame(reversedGameId);
        console.log('💰 [PAYOUT] Reversed team order lookup:', {
          reversedGameId,
          bets: reversedBets,
          hasBets: Object.keys(reversedBets).length > 0
        });
        
        if (Object.keys(reversedBets).length > 0) {
          betGameId = reversedGameId;
          betsFound = reversedBets;
        }
      }
    }
    
    // If still no bets found, search through all bets to find a match
    if (!betGameId || !betsFound) {
      console.log('💰 [PAYOUT] Searching through all bets for team match...');
      for (const [gameId, bets] of Object.entries(allBets)) {
        const gameBets = bets as { [teamId: string]: number };
        const teamIds = Object.keys(gameBets);
        const hasTeam1 = teamIds.includes(completedGame.teams.team1.id);
        const hasTeam2 = teamIds.includes(completedGame.teams.team2.id);
        
        if (hasTeam1 && hasTeam2) {
          console.log('💰 [PAYOUT] Found matching bets by team IDs:', { gameId, bets: gameBets });
          betGameId = gameId;
          betsFound = gameBets;
          break;
        }
      }
    }
    
    if (betGameId) {
      // Calculate team averages and get selected players for over/under bets
      const team1Standing = getTeamStanding(completedGame.teams.team1.id);
      const team2Standing = getTeamStanding(completedGame.teams.team2.id);
      const team1AvgPoints = getTeamAveragePoints(team1Standing);
      const team2AvgPoints = getTeamAveragePoints(team2Standing);
      
      // Get selected players for this game
      const team1Players = rosters[completedGame.teams.team1.id] || [];
      const team2Players = rosters[completedGame.teams.team2.id] || [];
      const selectedPlayers = selectPlayers(betGameId, team1Players, team2Players);
      
      // Process all bets (outcome and over/under)
      const payoutResults = Wallet.processAllBetsForGame(
        betGameId,
        completedGame,
        team1AvgPoints,
        team2AvgPoints,
        {
          team1Points: selectedPlayers.team1Points ? {
            id: selectedPlayers.team1Points.id,
            avgPoints: selectedPlayers.team1Points.avgPoints
          } : null,
          team1Assists: selectedPlayers.team1Assists ? {
            id: selectedPlayers.team1Assists.id,
            avgAssists: selectedPlayers.team1Assists.avgAssists
          } : null,
          team2Points: selectedPlayers.team2Points ? {
            id: selectedPlayers.team2Points.id,
            avgPoints: selectedPlayers.team2Points.avgPoints
          } : null,
          team2Assists: selectedPlayers.team2Assists ? {
            id: selectedPlayers.team2Assists.id,
            avgAssists: selectedPlayers.team2Assists.avgAssists
          } : null
        }
      );
      
      // Update credits
      const finalCredits = Wallet.getCredits();
      handleCreditsUpdate(finalCredits);
      
      // Show toasts for each payout result
      if (showToastCallback && payoutResults.length > 0) {
        payoutResults.forEach(result => {
          if (result.betType === 'outcome') {
        if (result.won) {
              const winningTeamName = completedGame.teams.team1.id === result.betKey
            ? completedGame.teams.team1.name 
            : completedGame.teams.team2.name;
            showToastCallback(
              `Won ${result.payout} credits on ${winningTeamName}! New total: ${result.newTotal}`,
              'success'
            );
        } else {
            showToastCallback(
              `Lost bet. Credits: ${result.newTotal}`,
              'info'
            );
          }
          } else if (result.betType === 'over-under') {
            if (result.won) {
              showToastCallback(
                `Won ${result.payout} credits on over/under bet! New total: ${result.newTotal}`,
                'success'
              );
            } else if (result.payout > 0) {
              // Push (refund)
              showToastCallback(
                `Push on over/under bet. Refunded ${result.payout} credits. New total: ${result.newTotal}`,
                'info'
              );
      } else {
              showToastCallback(
                `Lost over/under bet. Credits: ${result.newTotal}`,
                'info'
              );
            }
          }
        });
      } else if (payoutResults.length === 0 && window.Notification && Notification.permission === 'granted') {
        // Fallback to notification if no toast callback
        const winningTeamId = getWinningTeamId(completedGame);
        if (winningTeamId) {
          const winningTeamName = completedGame.teams.team1.id === winningTeamId
            ? completedGame.teams.team1.name
            : completedGame.teams.team2.name;
          new Notification('Bet Won!', {
            body: `You won credits on ${winningTeamName}!`,
            icon: '🎉'
          });
        }
      }
    } else {
      console.log('💰 [PAYOUT] No bets found for this game after exhaustive search');
    }
  }, [handleCreditsUpdate, getTeamStanding, rosters]);

  // Handle bet migration when game starts
  const handleGameStart = (gameData: GameData, slot: number) => {
    console.log('🎯 [MIGRATION] Game started, checking for bets to migrate:', {
      gameId: gameData.id,
      slot,
      team1: gameData.teams.team1.name,
      team2: gameData.teams.team2.name
    });
    
    const betResult = Wallet.findBetByTeams(gameData.teams.team1.id, gameData.teams.team2.id, slot);
    console.log('🎯 [MIGRATION] Bet search result:', betResult);
    
    if (betResult) {
      const betsBefore = Wallet.getBetsForGame(betResult.gameId);
      console.log('🎯 [MIGRATION] Bets before migration:', betsBefore);
      
      Wallet.migrateBet(betResult.gameId, gameData.id);
      
      const betsAfter = Wallet.getBetsForGame(gameData.id);
      console.log('🎯 [MIGRATION] Bets after migration:', betsAfter);
      console.log(`🎯 Migrated bets for ${gameData.teams.team1.name} vs ${gameData.teams.team2.name} from ${betResult.gameId} to game ${gameData.id}`);
    } else {
      console.log('🎯 [MIGRATION] No bets found to migrate for this game');
    }
  };

  // Initialize socket connection
  useEffect(() => {
    if (window.Notification && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      console.log('Connected to continuous game server');
      setConnectionStatus('Connected');
      setLoading(false);
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from server');
      setConnectionStatus('Disconnected');
    });

    newSocket.on('connect_error', (error) => {
      console.error('Connection error:', error);
      setConnectionStatus('Connection Error');
      setLoading(false);
    });

    newSocket.on('continuous-game-start', (data: any) => {
      console.log('New game started:', data);
      const { slot, game } = data;
      handleGameStart(game, slot);
      setActiveGames(prev => {
        const newGames = [...prev];
        newGames[slot] = game;
        return newGames;
      });
    });

    newSocket.on('continuous-game-update', (data: any) => {
      const { slot, game } = data;
      setActiveGames(prev => {
        const newGames = [...prev];
        const currentGame = newGames[slot];
        
        if (!currentGame || currentGame.id !== game.id) {
          newGames[slot] = game;
        } else {
          newGames[slot] = {
            ...currentGame,
            currentPossession: game.currentPossession,
            score: game.score,
            playerStats: game.playerStats,
            possessionLog: game.possessionLog,
            status: game.status,
            endTime: game.endTime,
            currentPossessionTeam: game.currentPossessionTeam
          };
        }
        
        return newGames;
      });
    });

    newSocket.on('continuous-game-complete', (data: any) => {
      console.log('🎮 [GAME COMPLETE] Event received:', {
        slot: data.slot,
        gameId: data.game?.id,
        team1: data.game?.teams?.team1?.name,
        team2: data.game?.teams?.team2?.name,
        score: data.game?.score ? `${data.game.score.team1}-${data.game.score.team2}` : 'N/A'
      });
      const { slot, game } = data;
      
      // Process payout with toast
      handleGamePayout(game, slot, showToast);
      
      // Update game state
      setCompletedGames(prev => {
        const newGames = [...prev];
        newGames[slot] = game;
        return newGames;
      });
      setActiveGames(prev => {
        const newGames = [...prev];
        newGames[slot] = null;
        return newGames;
      });
    });

    newSocket.on('continuous-games-current', (data: any) => {
      console.log('Current games status:', data);
      const newActiveGames = data.activeGames || [null, null];
      const newCompletedGames = data.completedGames || [null, null];
      const newNextGames = data.nextGames || [null, null];
      
      setActiveGames(newActiveGames);
      setCompletedGames(newCompletedGames);
      setNextGames(newNextGames);
      
      // Check all bets against completed games on page load/reconnect
      // Use setTimeout to ensure state is updated first
      setTimeout(async () => {
        await checkAllBetsAgainstCompletedGames(newCompletedGames, showToast);
      }, 100);
    });

    newSocket.on('continuous-next-games', (data: any) => {
      console.log('Next games updated:', data);
      setNextGames(data.nextGames || [null, null]);
    });

    return () => {
      newSocket.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array - functions are stable useCallbacks

  // Also check bets on component mount (in case user returns to page)
  useEffect(() => {
    // Wait a bit for socket connection and state to initialize
    const timer = setTimeout(async () => {
      const allBets = Wallet.getAllBets();
      if (Object.keys(allBets).length > 0) {
        console.log('🔍 [MOUNT CHECK] Found bets on mount, checking against completed games');
        // Pass empty array - the function will fetch from API if needed
        await checkAllBetsAgainstCompletedGames([], showToast);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []); // Only run on mount

  // Handle hash navigation - scroll to anchor when page loads with hash or hash changes
  useEffect(() => {
    const hash = location.hash;
    if (hash && !loading) {
      // Wait for games to render, then scroll to anchor
      const scrollToAnchor = () => {
        const element = document.querySelector(hash);
        if (element) {
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - 100;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
          return true;
        }
        return false;
      };

      // Try immediately, then retry with increasing delays if element not found
      let timer1: NodeJS.Timeout | null = null;
      let timer2: NodeJS.Timeout | null = null;

      if (!scrollToAnchor()) {
        timer1 = setTimeout(() => {
          if (!scrollToAnchor()) {
            timer2 = setTimeout(() => {
              scrollToAnchor();
            }, 500);
          }
        }, 200);
      }

      return () => {
        if (timer1) clearTimeout(timer1);
        if (timer2) clearTimeout(timer2);
      };
    }
  }, [location.hash, loading]);

  // Also handle hash changes via window hashchange event (for same-page navigation)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash && !loading) {
        setTimeout(() => {
          const element = document.querySelector(hash);
          if (element) {
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - 100;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [loading]);

  // Scroll to top when navigating to games page without a hash
  useEffect(() => {
    if (!loading && !location.hash) {
      // Small delay to ensure page has rendered
      const timer = setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.hash, loading]);

  // Game component using original Webflow styling
  const GameComponent: React.FC<{ 
    game: GameData | null; 
    nextGame?: NextGame | null;
    gameId: string; 
    isFirst?: boolean;
    slotIndex: number;
  }> = ({ game, nextGame, gameId, isFirst = false, slotIndex }) => {
    const iconsContainerRef = useRef<SVGGElement>(null);
    const [team1BetAmount, setTeam1BetAmount] = useState(0);
    const [team2BetAmount, setTeam2BetAmount] = useState(0);
    
    // Get bet indicator and color for team/player stats (threshold-based)
    const getBetInfo = useMemo(() => {
      if (!game) return () => ({ indicator: '', color: '' });
      
      const betGameId = game.id;
      const bets = Wallet.getBetsForGame(betGameId);
      const nextGameId = `next-${slotIndex}-${game.teams.team1.id}-${game.teams.team2.id}`;
      const nextBets = Wallet.getBetsForGame(nextGameId);
      const allBets = { ...bets, ...nextBets };
      
      // Get team standings for thresholds
      const team1Standing = getTeamStanding(game.teams.team1.id);
      const team2Standing = getTeamStanding(game.teams.team2.id);
      const team1AvgPoints = getTeamAveragePoints(team1Standing);
      const team2AvgPoints = getTeamAveragePoints(team2Standing);
      
      // Get selected players for player bets
      const team1Players = rosters[game.teams.team1.id] || [];
      const team2Players = rosters[game.teams.team2.id] || [];
      const selectedPlayers = selectPlayers(betGameId, team1Players, team2Players);
      
      return (betType: string): { indicator: string; color: string } => {
        const overKey = `${betType}-over`;
        const underKey = `${betType}-under`;
        const overBet = allBets[overKey] || 0;
        const underBet = allBets[underKey] || 0;
        
        if (overBet > 0 || underBet > 0) {
          let threshold = 0;
          
          // Determine threshold based on bet type
          if (betType === 'team1-pts') {
            threshold = team1AvgPoints;
          } else if (betType === 'team2-pts') {
            threshold = team2AvgPoints;
          } else if (betType.startsWith('player-') && betType.includes('-pts')) {
            const playerId = betType.replace('player-', '').replace('-pts', '');
            if (selectedPlayers.team1Points?.id === playerId) {
              threshold = Math.round(selectedPlayers.team1Points.avgPoints);
            } else if (selectedPlayers.team2Points?.id === playerId) {
              threshold = Math.round(selectedPlayers.team2Points.avgPoints);
            } else {
              // Fallback: try to find by matching player ID in rosters
              const allPlayers = [...team1Players, ...team2Players];
              const player = allPlayers.find(p => p.id === playerId);
              if (player) {
                threshold = Math.round(player.avgPoints);
              }
            }
          } else if (betType.startsWith('player-') && betType.includes('-ast')) {
            const playerId = betType.replace('player-', '').replace('-ast', '');
            if (selectedPlayers.team1Assists?.id === playerId) {
              threshold = Math.round(selectedPlayers.team1Assists.avgAssists);
            } else if (selectedPlayers.team2Assists?.id === playerId) {
              threshold = Math.round(selectedPlayers.team2Assists.avgAssists);
            } else {
              // Fallback: try to find by matching player ID in rosters
              const allPlayers = [...team1Players, ...team2Players];
              const player = allPlayers.find(p => p.id === playerId);
              if (player) {
                threshold = Math.round(player.avgAssists);
              }
            }
          }
          
          if (overBet > 0) {
            return { indicator: `(▲ ${threshold})`, color: '#128700' };
          } else if (underBet > 0) {
            return { indicator: `(▼ ${threshold})`, color: '#C91111' };
          }
        }
        return { indicator: '', color: '' };
      };
    }, [game, slotIndex, rosters, getTeamStanding]);

    // Memoize game data for court visualization
    const gameTeams = useMemo(() => {
      if (!game?.teams) return null;
      return {
        team1: {
          id: game.teams.team1.id,
          name: game.teams.team1.name,
          color: game.teams.team1.color
        },
        team2: {
          id: game.teams.team2.id,
          name: game.teams.team2.name,
          color: game.teams.team2.color
        }
      };
    }, [game?.teams]);

    const teamColors = useMemo(() => {
      if (!game?.teams) return null;
      return {
        team1: game.teams.team1.color,
        team2: game.teams.team2.color
      };
    }, [game?.teams]);

    const possessionLog = useMemo(() => {
      return game?.possessionLog || [];
    }, [game?.possessionLog]);

    // Calculate bet amounts for each team
    const calculateTeamBetAmounts = useCallback(() => {
      let team1Bet = 0;
      let team2Bet = 0;
      
      if (game) {
        // Check for bets on active/completed game
        const gameBets = Wallet.getBetsForGame(game.id);
        team1Bet = gameBets[game.teams.team1.id] || 0;
        team2Bet = gameBets[game.teams.team2.id] || 0;
        
        // Also check for next game pattern (in case bet wasn't migrated yet)
        if (team1Bet === 0 && team2Bet === 0 && game.teams) {
          const nextGameId = `next-${slotIndex}-${game.teams.team1.id}-${game.teams.team2.id}`;
          const nextBets = Wallet.getBetsForGame(nextGameId);
          team1Bet = nextBets[game.teams.team1.id] || 0;
          team2Bet = nextBets[game.teams.team2.id] || 0;
        }
      } else if (nextGame) {
        // Check for bets on next game
        const nextGameId = `next-${slotIndex}-${nextGame.team1.id}-${nextGame.team2.id}`;
        const nextBets = Wallet.getBetsForGame(nextGameId);
        team1Bet = nextBets[nextGame.team1.id] || 0;
        team2Bet = nextBets[nextGame.team2.id] || 0;
      }
      
      return { team1Bet, team2Bet };
    }, [game, nextGame, slotIndex]);

    // Update bet amounts on mount and when game/nextGame changes
    useEffect(() => {
      const { team1Bet, team2Bet } = calculateTeamBetAmounts();
      setTeam1BetAmount(team1Bet);
      setTeam2BetAmount(team2Bet);
    }, [calculateTeamBetAmounts]);

    // Poll for bet changes every 1 second
    useEffect(() => {
      const interval = setInterval(() => {
        const { team1Bet, team2Bet } = calculateTeamBetAmounts();
        if (team1Bet !== team1BetAmount || team2Bet !== team2BetAmount) {
          setTeam1BetAmount(team1Bet);
          setTeam2BetAmount(team2Bet);
        }
      }, 1000);

      return () => clearInterval(interval);
    }, [calculateTeamBetAmounts, team1BetAmount, team2BetAmount]);

    // Format possession descriptions with player name highlighting
    const formatPossessionDescription = (description: string) => {
      if (!game?.teams) return description;
      
      const team1Players = game.playerStats.team1.map(p => p.name);
      const team2Players = game.playerStats.team2.map(p => p.name);
      
      let formattedDescription = description;
      
      team1Players.forEach(playerName => {
        const escapedName = playerName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const playerRegex = new RegExp(`(^|\\s|\\b)${escapedName}(\\s|\\b|[.,!?]|$)`, 'g');
        formattedDescription = formattedDescription.replace(playerRegex, 
          `$1<span style="color: ${game.teams.team1.color}; font-weight: bold;">${playerName}</span>$2`
        );
      });
      
      team2Players.forEach(playerName => {
        const escapedName = playerName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const playerRegex = new RegExp(`(^|\\s|\\b)${escapedName}(\\s|\\b|[.,!?]|$)`, 'g');
        formattedDescription = formattedDescription.replace(playerRegex, 
          `$1<span style="color: ${game.teams.team2.color}; font-weight: bold;">${playerName}</span>$2`
        );
      });
      
      return formattedDescription;
    };

    return (
      <div id={gameId} className={`game-wrapper ${isFirst ? 'game-1' : ''}`} style={{ width: '100%', scrollMarginTop: '100px' }}>
        {!isFirst && <div className="divider game"></div>}
        <div className="game" style={{ width: '100%' }}>
          <div className="game-sim" style={{ display: 'grid', gridTemplateColumns: '0.45fr 0.55fr', gap: '2rem', width: '100%', minHeight: '0' }}>
            {/* Left Column - Game Content (45%) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%', minWidth: '0' }}>
              <div className="score">
                <div className="game-side">
                  <div className="game-score" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    {game && (() => {
                      const betInfo = getBetInfo('team1-pts');
                      return betInfo.indicator ? (
                        <span style={{ color: betInfo.color, fontSize: '0.875rem', fontFamily: 'monospace' }}>
                          {betInfo.indicator}
                        </span>
                      ) : null;
                    })()}
                    {game ? game.score.team1 : (nextGame ? '0' : '---')}
                  </div>
                  <div className="game-team">
                    <div>{game ? game.teams.team1.name : (nextGame ? nextGame.team1.name : 'Waiting...')}</div>
                    <div 
                      className="team-dot"
                      style={{
                        backgroundColor: game ? game.teams.team1.color : (nextGame ? nextGame.team1.color : '#666'),
                        border: game?.currentPossessionTeam === 'team1' ? `2px solid ${game.teams.team1.color}` : 'none'
                      }}
                    ></div>
                  </div>
                </div>
                <div className="vs-label">vs</div>
                <div className="game-side">
                  <div className="game-score right" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', justifyContent: 'flex-end' }}>
                    {game && (() => {
                      const betInfo = getBetInfo('team2-pts');
                      return betInfo.indicator ? (
                        <span style={{ color: betInfo.color, fontSize: '0.875rem', fontFamily: 'monospace' }}>
                          {betInfo.indicator}
                        </span>
                      ) : null;
                    })()}
                    {game ? game.score.team2 : (nextGame ? '0' : '---')}
                  </div>
                  <div className="game-team">
                    <div 
                      className="team-dot"
                      style={{
                        backgroundColor: game ? game.teams.team2.color : (nextGame ? nextGame.team2.color : '#666'),
                        border: game?.currentPossessionTeam === 'team2' ? `2px solid ${game.teams.team2.color}` : 'none'
                      }}
                    ></div>
                    <div>{game ? game.teams.team2.name : (nextGame ? nextGame.team2.name : 'Waiting...')}</div>
                  </div>
                </div>
              </div>

              {/* Court with live visualization */}
              <div className="game-court" style={{ position: 'relative', background: '#000', borderRadius: '8px' }}>
                <svg 
                  width="100%" 
                  viewBox="0 0 337 157" 
                  style={{ width: '100%', height: 'auto' }}
                  preserveAspectRatio="xMidYMid meet"
                >
                  <g clipPath="url(#clip0_18_2)">
                    {/* Team zones with dynamic colors */}
                    <path id="t2_LongRange" d="M225.5 121.5L224.923 34L278.431 0.358368L314.918 0.358371L253 38.5L253 117.5L315.5 156H278.235L225.5 121.5Z" 
                          fill={game ? game.teams.team2.color : '#4ECDC4'} fillOpacity="0"/>
                    <path id="t2_Deep" d="M195 0.499972L168.5 0.499969L168.5 -3.05176e-05L315.5 -1.76664e-05L314.918 0.358381L278.431 0.358378L224.923 34L225.5 121.5L278.235 156L195 156L195 0.499972Z" 
                          fill={game ? game.teams.team2.color : '#4ECDC4'} fillOpacity="0"/>
                    <path id="t1_LongRange" d="M111.5 35L112.077 122.5L58.5693 156.142H22.082L84 118V39L21.5 0.5H58.7646L111.5 35Z" 
                          fill={game ? game.teams.team1.color : '#FF6B6B'} fillOpacity="0"/>
                    <path id="t1_Deep" d="M142 156H168.5V156.5H21.5L22.082 156.142H58.5693L112.077 122.5L111.5 35L58.7646 0.5H142V156Z" 
                          fill={game ? game.teams.team1.color : '#FF6B6B'} fillOpacity="0"/>
                    
                    {/* Court outline */}
                    <rect x="1" y="57" width="83" height="39" fill="#2E2E2E"/>
                    <rect x="252" y="57" width="84" height="39" fill="#2E2E2E"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M337 19.625V137.375L315.938 157H21.0625L0 137.375V19.625L21.0625 0H315.938L337 19.625ZM84.75 118.03L22.7734 156H168V115.059L149.941 97H84.75V118.03ZM169 114.883V156H313.477L251.5 118.03V97H186.883L169 114.883ZM252.5 117.47L315.392 156H315.544L336 136.939V97H252.5V117.47ZM1 136.939L21.2188 155.778L83.75 117.47V97H1V136.939ZM168 113.645V97H151.355L168 113.645ZM169 113.469L185.469 97H169V113.469ZM1 69.7588V72.5879L14 59.5879V61.0029L1 74.0029V76.8311L14 63.8311V65.2451L1 78.2451V81.0732L14 68.0732V69.4873L1 82.4873V85.3154L14 72.3154V73.7295L1 86.7295V89.5576L14 76.5576V77.9727L1 90.9727V93.8018L14 80.8018V82.2158L1 95.2158V96H3.04395L14 85.0439V86.458L4.45801 96H7.28613L14 89.2861V90.7002L8.7002 96H11.5283L14 93.5283V94.9424L12.9424 96H83.75V58H12.7588L1 69.7588ZM84.75 96H148.94L129.882 76.9414L148.823 58H84.75V96ZM131.296 76.9414L150.355 96H168V58H150.238L131.296 76.9414ZM169 96H186.469L205.527 76.9414L186.586 58H169V96ZM206.941 76.9414L187.883 96H251.5V58H188L206.941 76.9414ZM335.942 96H336V95.9424L335.942 96ZM331.7 96H334.528L336 94.5283V91.7002L331.7 96ZM327.458 96H330.286L336 90.2861V87.458L327.458 96ZM323.216 96H326.044L336 86.0439V83.2158L323.216 96ZM252.5 96H323V94.8018L336 81.8018V78.9727L323 91.9727V90.5576L336 77.5576V74.7295L323 87.7295V86.3154L336 73.3154V70.4873L323 83.4873V82.0732L336 69.0732V66.2451L323 79.2451V77.8311L336 64.8311V62.0029L323 75.0029V73.5879L336 60.5879V58H335.759L323 70.7588V69.3447L334.345 58H331.517L323 66.5166V65.1025L330.103 58H327.274L323 62.2744V60.8604L325.86 58H323.032L323 58.0322V58H252.5V96ZM1 65.5166V68.3447L11.3447 58H8.5166L1 65.5166ZM1 61.2744V64.1025L7.10254 58H4.27441L1 61.2744ZM1 59.8604L2.86035 58H1V59.8604ZM1 20.0596V57H83.75V39.5293L21.2188 1.2207L1 20.0596ZM84.75 38.9697V57H149.824L168 38.8232V1H22.7734L84.75 38.9697ZM151.238 57H168V40.2373L151.238 57ZM169 57H185.586L169 40.4141V57ZM169 39L187 57H251.5V38.9697L313.477 1H169V39ZM252.5 39.5293V57H336V20.0596L315.544 1H315.392L252.5 39.5293Z" fill="white"/>
                    
                    {/* Icons container for possession visualization */}
                    <g ref={iconsContainerRef} id="icons-container"></g>
                  </g>
                  <defs>
                    <clipPath id="clip0_18_2">
                      <rect width="337" height="157" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>

                {/* DirectDOMIconManager for live possession icons */}
                {game && (
                  <DirectDOMIconManager
                    possessionLog={possessionLog}
                    gameId={game.id}
                    gameTeams={gameTeams}
                    teamColors={teamColors}
                    containerRef={iconsContainerRef}
                    maxPossessions={20}
                  />
                )}
              </div>

              {/* Play-by-play with original styling */}
              <div className="pbp-wrapper">
                <div className="pbp-scroller">
                  <div className="restruct-content">
                    <div className="restruct-label">
                      {game?.status === 'live' ? 'Live Play-by-Play' : 
                       game ? 'Game Completed' : 
                       nextGame ? 'Next Matchup' : 'Waiting for Game...'}
                    </div>
                  </div>
                  
                  {game && possessionLog.length > 0 ? (
                    possessionLog.slice(-8).reverse().map((possession, index) => (
                      <div key={index} className="rule">
                        <div className="rule-name">
                          <div className="w-embed">
                            <svg xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                              <path d="M4 3H7V4H4V7H3V4H0V3H3V0H4V3Z" fill="white"></path>
                            </svg>
                          </div>
                          <p 
                          className="rule-text no-title" 
                          dangerouslySetInnerHTML={{ __html: formatPossessionDescription(possession.description) }}
                        />
                        </div>
                      </div>
                    ))
                  ) : nextGame ? (
                    <div className="rule">
                      <div className="rule-name">
                        <div className="w-embed">
                          <svg xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                            <path d="M4 3H7V4H4V7H3V4H0V3H3V0H4V3Z" fill="white"></path>
                          </svg>
                        </div>
                        <div>Upcoming Matchup</div>
                      </div>
                      <p className="rule-text no-title">
                        {nextGame.team1.name} vs {nextGame.team2.name} - Game starts with next cycle
                      </p>
                    </div>
                  ) : (
                    <div className="rule">
                      <div className="rule-name">
                        <div className="w-embed">
                          <svg xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none">
                            <path d="M4 3H7V4H4V7H3V4H0V3H3V0H4V3Z" fill="white"></path>
                          </svg>
                        </div>
                        <div>Arena Status</div>
                      </div>
                      <p className="rule-text no-title">Arena ready - waiting for next scheduled matchup.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Team Rosters (55%) */}
            <div className="game-teams" style={{ width: '100%', minWidth: '0' }}>
              <div className="table--team">
                <div className="thead" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>{game ? game.teams.team1.name : (nextGame ? nextGame.team1.name : 'Team 1')}</div>
                  {team1BetAmount > 0 && (
                    <div style={{ color: '#9ca3af', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                      {team1BetAmount} Σ
                    </div>
                  )}
                </div>
                {game ? (() => {
                  // Get selected players for this game to check for bets
                  const team1Players = rosters[game.teams.team1.id] || [];
                  const team2Players = rosters[game.teams.team2.id] || [];
                  const selectedPlayers = selectPlayers(game.id, team1Players, team2Players);
                  
                  return game.playerStats.team1.map((player, index) => {
                    // Try to find player ID by matching name to roster if ID is missing
                    let playerId = player.id;
                    if (!playerId) {
                      const allPlayers = [...team1Players, ...team2Players];
                      const rosterPlayer = allPlayers.find(p => p.name === player.name);
                      if (rosterPlayer) {
                        playerId = rosterPlayer.id;
                      }
                    }
                    
                    return (
                    <div key={`t1-${index}`} className="trow player w-inline-block">
                      <div className="player-name" style={{ flex: '1' }}>{player.name}</div>
                      <div className="player-mech" style={{ flex: '1', textAlign: 'left' }}>{player.mechClass}</div>
                      <div className="player-stats" style={{ display: 'flex', gap: '0.75rem', flex: '2' }}>
                          <div style={{ flex: '1', textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                            {playerId && (() => {
                              const betInfo = getBetInfo(`player-${playerId}-pts`);
                              return betInfo.indicator && (
                                <span style={{ color: betInfo.color, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                  {betInfo.indicator}
                                </span>
                              );
                            })()}
                            {player.points}
                          </div>
                        <div style={{ flex: '1', textAlign: 'right' }}>{player.rebounds}</div>
                          <div style={{ flex: '1', textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                            {playerId && (() => {
                              const betInfo = getBetInfo(`player-${playerId}-ast`);
                              return betInfo.indicator && (
                                <span style={{ color: betInfo.color, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                  {betInfo.indicator}
                                </span>
                              );
                            })()}
                            {player.assists}
                      </div>
                    </div>
                      </div>
                    );
                  });
                })() : (
                  Array.from({ length: 5 }).map((_, index) => (
                    <div key={`placeholder-t1-${index}`} className="trow player w-inline-block">
                      <div className="player-name" style={{ flex: '1' }}>---</div>
                      <div className="player-mech" style={{ flex: '1', textAlign: 'left' }}>---</div>
                      <div className="player-stats" style={{ display: 'flex', gap: '0.75rem', flex: '2' }}>
                        <div style={{ flex: '1', textAlign: 'right' }}>-</div>
                        <div style={{ flex: '1', textAlign: 'right' }}>-</div>
                        <div style={{ flex: '1', textAlign: 'right' }}>-</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <div className="table--team">
                <div className="thead" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>{game ? game.teams.team2.name : (nextGame ? nextGame.team2.name : 'Team 2')}</div>
                  {team2BetAmount > 0 && (
                    <div style={{ color: '#9ca3af', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                      {team2BetAmount} Σ
                    </div>
                  )}
                </div>
                {game ? (() => {
                  // Get selected players for this game to check for bets
                  const team1Players = rosters[game.teams.team1.id] || [];
                  const team2Players = rosters[game.teams.team2.id] || [];
                  const selectedPlayers = selectPlayers(game.id, team1Players, team2Players);
                  
                  return game.playerStats.team2.map((player, index) => {
                    // Try to find player ID by matching name to roster if ID is missing
                    let playerId = player.id;
                    if (!playerId) {
                      const allPlayers = [...team1Players, ...team2Players];
                      const rosterPlayer = allPlayers.find(p => p.name === player.name);
                      if (rosterPlayer) {
                        playerId = rosterPlayer.id;
                      }
                    }
                    
                    return (
                    <div key={`t2-${index}`} className="trow player w-inline-block">
                      <div className="player-name" style={{ flex: '1' }}>{player.name}</div>
                      <div className="player-mech" style={{ flex: '1', textAlign: 'left' }}>{player.mechClass}</div>
                      <div className="player-stats" style={{ display: 'flex', gap: '0.75rem', flex: '2' }}>
                          <div style={{ flex: '1', textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                            {playerId && (() => {
                              const betInfo = getBetInfo(`player-${playerId}-pts`);
                              return betInfo.indicator && (
                                <span style={{ color: betInfo.color, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                  {betInfo.indicator}
                                </span>
                              );
                            })()}
                            {player.points}
                          </div>
                        <div style={{ flex: '1', textAlign: 'right' }}>{player.rebounds}</div>
                          <div style={{ flex: '1', textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                            {playerId && (() => {
                              const betInfo = getBetInfo(`player-${playerId}-ast`);
                              return betInfo.indicator && (
                                <span style={{ color: betInfo.color, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                  {betInfo.indicator}
                                </span>
                              );
                            })()}
                            {player.assists}
                      </div>
                    </div>
                      </div>
                    );
                  });
                })() : (
                  Array.from({ length: 5 }).map((_, index) => (
                    <div key={`placeholder-t2-${index}`} className="trow player w-inline-block">
                      <div className="player-name" style={{ flex: '1' }}>---</div>
                      <div className="player-mech" style={{ flex: '1', textAlign: 'left' }}>---</div>
                      <div className="player-stats" style={{ display: 'flex', gap: '0.75rem', flex: '2' }}>
                        <div style={{ flex: '1', textAlign: 'right' }}>-</div>
                        <div style={{ flex: '1', textAlign: 'right' }}>-</div>
                        <div style={{ flex: '1', textAlign: 'right' }}>-</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="container--interior">
          <LiveGamesSidebar />
          <div className="interior-wrapper">
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '16rem' }}>
              <div style={{ color: '#22d3ee' }}>
                <div style={{ animation: 'pulse 2s infinite' }}>🔗 {connectionStatus}</div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Get current games for display
  const game1 = activeGames[0] || completedGames[0];
  const game2 = activeGames[1] || completedGames[1];
  const nextGame1 = nextGames[0];
  const nextGame2 = nextGames[1];

  return (
    <>
      <Navigation />
      
      <div className="container--interior">
        <LiveGamesSidebar />
        
        <div className="interior-wrapper">
          {/* Betting Panel */}
          <BettingPanel 
            nextGames={nextGames} 
            onCreditsUpdate={handleCreditsUpdate}
          />

          {/* Game 1 */}
          <GameComponent 
            game={game1} 
            nextGame={nextGame1}
            gameId="game_1" 
            isFirst={true}
            slotIndex={0}
          />

          {/* Game 2 */}
          <GameComponent 
            game={game2} 
            nextGame={nextGame2}
            gameId="game_2" 
            isFirst={false}
            slotIndex={1}
          />
          
          <p className="copyright"><span className="copyright-icon">© </span>2077 Neo Vegas CORP Trust</p>
          <p className="disclaimer">* All matches simulated using advanced mech combat algorithms with real-time possession tracking, continuous game simulation, and live betting integration.</p>
        </div>
      </div>

      {/* Toast Notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            id={toast.id}
            message={toast.message}
            type={toast.type}
            onDismiss={dismissToast}
          />
        ))}
      </div>
    </>
  );
};

export default GamesPage; 